import { useState, useEffect } from 'react';
import { profileApiService } from '../services/profileApiService';
import { OfficialApiInbox } from '../types/template';

/**
 * Hook para carregar fotos de perfil de múltiplos números da API Oficial
 */
interface ProfilePictureState {
  url: string | null;
  loading: boolean;
  error: string | null;
  isBlocked: boolean; // Nova propriedade para detectar conta bloqueada
}

export function useProfilePictures(inboxes: OfficialApiInbox[]) {
  const [profilePictures, setProfilePictures] = useState<Map<string, ProfilePictureState>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Verifica se o erro indica que a conta está bloqueada pela Meta
   */
  const isAccountBlockedError = (error: any): boolean => {
    if (!error) return false;
    
    const errorMessage = error.message || error.toString();
    const errorCode = error.code || error.response?.data?.code;
    
    // Verificar código específico da Meta para conta bloqueada
    if (errorCode === 131031) {
      // console.log('🚫 Código 131031 detectado - Conta bloqueada');
      return true;
    }
    
    // Verificar mensagem de erro
    if (errorMessage.includes('Business Account locked') || 
        errorMessage.includes('Business account has been locked') ||
        errorMessage.includes('#131031') ||
        errorMessage.includes('131031')) {
      // console.log('🚫 Mensagem de conta bloqueada detectada');
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    const loadProfilePictures = async () => {
      if (inboxes.length === 0) {
        setProfilePictures(new Map());
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // console.log(`📸 Carregando fotos de perfil para ${inboxes.length} número(s)...`);
        
        // Inicializar todos os inboxes como loading
        const initialMap = new Map<string, ProfilePictureState>();
        inboxes.forEach(inbox => {
          initialMap.set(inbox.providerConfig.phoneNumberId, {
            url: null,
            loading: true,
            error: null,
            isBlocked: false
          });
        });
        setProfilePictures(initialMap);
        
        // Carregar fotos em paralelo com timeout individual
        const profilePromises = inboxes.map(async (inbox) => {
          const phoneNumberId = inbox.providerConfig.phoneNumberId;
          
          try {
            // console.log(`📥 Buscando perfil para ${inbox.name} (${phoneNumberId})`);
            
            // Criar timeout de 5 segundos
            const timeoutPromise = new Promise<null>((_, reject) => {
              setTimeout(() => reject(new Error('Timeout após 5 segundos')), 5000);
            });
            
            // Promise para buscar o perfil
            const profilePromise = profileApiService.getBusinessProfile(phoneNumberId);
            
            // Race entre a requisição e o timeout
            const profile = await Promise.race([profilePromise, timeoutPromise]);
            
            if (profile?.profile_picture_url) {
              // Atualizar estado para sucesso
              setProfilePictures((prev: Map<string, ProfilePictureState>) => {
                const newMap = new Map(prev);
                newMap.set(phoneNumberId, {
                  url: profile.profile_picture_url || null,
                  loading: false,
                  error: null,
                  isBlocked: false
                });
                return newMap;
              });
              // console.log(`✅ Foto encontrada para ${inbox.name}`);
            } else {
              // Atualizar estado para sem foto
              setProfilePictures((prev: Map<string, ProfilePictureState>) => {
                const newMap = new Map(prev);
                newMap.set(phoneNumberId, {
                  url: null,
                  loading: false,
                  error: null,
                  isBlocked: false
                });
                return newMap;
              });
              // console.log(`⚠️ Nenhuma foto de perfil para ${inbox.name}`);
            }
          } catch (error: any) {
            // Log do erro completo para debug
            // console.warn(`⚠️ Erro ao carregar perfil para ${inbox.name}:`, {
            //   message: error.message,
            //   code: error.code,
            //   response: error.response?.data
            // });
            
            // Verificar se é erro de conta bloqueada
            const isBlocked = isAccountBlockedError(error);
            
            // Atualizar estado para erro/timeout
            setProfilePictures((prev: Map<string, ProfilePictureState>) => {
              const newMap = new Map(prev);
              newMap.set(phoneNumberId, {
                url: null,
                loading: false,
                error: error.message || 'Erro ao carregar foto',
                isBlocked: isBlocked
              });
              return newMap;
            });
            
            if (isBlocked) {
              console.error(`🚫 CONTA BLOQUEADA: ${inbox.name} (ID: ${phoneNumberId})`);
            }
          }
        });

        // Aguardar todas as requisitções (incluindo timeouts)
        await Promise.allSettled(profilePromises);
        
        // console.log(`✅ Carregamento de fotos de perfil finalizado`);
        
      } catch (error: any) {
        console.error('❌ Erro geral ao carregar fotos de perfil:', error);
        setError(error.message || 'Erro ao carregar fotos de perfil');
      } finally {
        setLoading(false);
      }
    };

    loadProfilePictures();
  }, [inboxes]);

  /**
   * Obtém a URL da foto de perfil para um número específico
   */
  const getProfilePicture = (phoneNumberId: string): string | null => {
    const state = profilePictures.get(phoneNumberId);
    return state?.url || null;
  };

  /**
   * Verifica se uma foto de perfil está disponível para um número
   */
  const hasProfilePicture = (phoneNumberId: string): boolean => {
    const state = profilePictures.get(phoneNumberId);
    return !!(state?.url && !state.loading);
  };

  /**
   * Verifica se um número está carregando a foto de perfil
   */
  const isLoadingProfilePicture = (phoneNumberId: string): boolean => {
    const state = profilePictures.get(phoneNumberId);
    return state?.loading || false;
  };

  /**
   * Verifica se uma conta está bloqueada pela Meta
   */
  const isAccountBlocked = (phoneNumberId: string): boolean => {
    const state = profilePictures.get(phoneNumberId);
    return state?.isBlocked || false;
  };

  /**
   * Obtém o estado completo de um número
   */
  const getProfilePictureState = (phoneNumberId: string): ProfilePictureState | null => {
    return profilePictures.get(phoneNumberId) || null;
  };

  /**
   * Recarrega as fotos de perfil
   */
  const refreshProfilePictures = () => {
    setProfilePictures(new Map());
    setError(null);
    // O useEffect será executado novamente devido à mudança no estado
  };

  return {
    profilePictures,
    loading,
    error,
    getProfilePicture,
    hasProfilePicture,
    isLoadingProfilePicture,
    isAccountBlocked,
    getProfilePictureState,
    refreshProfilePictures
  };
}
